/**
 * Attachments (intake/spec §3.5, §4): drop/browse zone → attachment tiles with per-tile upload
 * progress, retry-on-fail, and remove. Client-side validates type (image/* or PDF) + size (<10MB)
 * before upload (intake/spec §5); the server re-validates (NFR-10). Uploaded files become S3 keys
 * carried in the submit body's `attachments` (@td/shared).
 */
import { useCallback, useId, useRef, useState } from "react";
import { uploadAttachment } from "../api/index.js";
import { MAX_ATTACHMENTS } from "@td/shared";
import { CloseIcon, FileIcon, AlertIcon } from "./icons.js";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = "image/*,application/pdf";

type TileStatus = "uploading" | "done" | "error";

export interface AttachmentTile {
  localId: string;
  file: File;
  status: TileStatus;
  progress: number;
  /** Object URL for image previews (revoked on remove). */
  previewUrl?: string;
  /** The S3 key once uploaded — this is what goes in the submit body. */
  key?: string;
  error?: string;
}

interface Props {
  tiles: AttachmentTile[];
  onChange: (tiles: AttachmentTile[]) => void;
}

export function AttachmentUploader({ tiles, onChange }: Props): JSX.Element {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [rejectMsg, setRejectMsg] = useState<string | null>(null);
  const tilesRef = useRef(tiles);
  tilesRef.current = tiles;

  const patch = useCallback(
    (localId: string, next: Partial<AttachmentTile>) => {
      onChange(tilesRef.current.map((t) => (t.localId === localId ? { ...t, ...next } : t)));
    },
    [onChange],
  );

  const startUpload = useCallback(
    (tile: AttachmentTile) => {
      patch(tile.localId, { status: "uploading", progress: 0, error: undefined });
      uploadAttachment(tile.file, (pct) => patch(tile.localId, { progress: pct }))
        .then((key) => patch(tile.localId, { status: "done", progress: 100, key }))
        .catch(() => patch(tile.localId, { status: "error", error: "Upload failed — tap retry." }));
    },
    [patch],
  );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      setRejectMsg(null);
      const incoming = Array.from(files);
      const room = MAX_ATTACHMENTS - tilesRef.current.length;
      if (room <= 0) {
        setRejectMsg(`You can add up to ${MAX_ATTACHMENTS} files.`);
        return;
      }
      const accepted: AttachmentTile[] = [];
      for (const file of incoming.slice(0, room)) {
        const isImage = file.type.startsWith("image/");
        const isPdf = file.type === "application/pdf";
        if (!isImage && !isPdf) {
          setRejectMsg("Photos must be an image or PDF.");
          continue;
        }
        if (file.size > MAX_BYTES) {
          setRejectMsg("Photos must be under 10 MB and be an image or PDF.");
          continue;
        }
        accepted.push({
          localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          status: "uploading",
          progress: 0,
          previewUrl: isImage ? URL.createObjectURL(file) : undefined,
        });
      }
      if (incoming.length > room) {
        setRejectMsg(
          `Only the first ${room} file${room === 1 ? "" : "s"} were added (max ${MAX_ATTACHMENTS}).`,
        );
      }
      if (!accepted.length) return;
      const nextTiles = [...tilesRef.current, ...accepted];
      // Keep the ref in sync BEFORE kicking off uploads: startUpload -> patch reads
      // tilesRef.current synchronously, and no re-render has happened yet, so without
      // this the first patch would map over the stale pre-add array and clobber the
      // just-added tiles (dropping the uploaded S3 key from the submit body).
      tilesRef.current = nextTiles;
      onChange(nextTiles);
      accepted.forEach(startUpload);
    },
    [onChange, startUpload],
  );

  const removeTile = (tile: AttachmentTile) => {
    if (tile.previewUrl) URL.revokeObjectURL(tile.previewUrl);
    onChange(tilesRef.current.filter((t) => t.localId !== tile.localId));
  };

  const full = tiles.length >= MAX_ATTACHMENTS;

  return (
    <div className="td-field">
      <span className="td-field__label" id={`${inputId}-label`}>
        Add photos or a file
        <span className="td-field__optional"> (optional)</span>
      </span>
      <p className="td-field__helper" id={`${inputId}-helper`}>
        Up to {MAX_ATTACHMENTS}. Images or PDF, under 10 MB each.
      </p>

      {!full ? (
        <div
          className={`td-dropzone${dragOver ? " is-dragover" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            addFiles(e.dataTransfer.files);
          }}
        >
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            className="td-sr-only"
            accept={ACCEPT}
            multiple
            aria-label="Add photos or a file (optional)"
            aria-describedby={`${inputId}-helper`}
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            className="td-dropzone__btn"
            onClick={() => inputRef.current?.click()}
          >
            <FileIcon />
            <span>Add photos or a file</span>
          </button>
          <span className="td-dropzone__hint">or drop them here</span>
        </div>
      ) : null}

      {rejectMsg ? (
        <p className="td-field__error" role="alert">
          <AlertIcon width={16} height={16} />
          <span>{rejectMsg}</span>
        </p>
      ) : null}

      {tiles.length ? (
        <ul className="td-attachments" aria-label="Added files">
          {tiles.map((t) => (
            <li key={t.localId} className={`td-attachment td-attachment--${t.status}`}>
              <span className="td-attachment__thumb" aria-hidden="true">
                {t.previewUrl ? (
                  <img src={t.previewUrl} alt="" />
                ) : (
                  <FileIcon width={22} height={22} />
                )}
              </span>
              <span className="td-attachment__meta">
                <span className="td-attachment__name" title={t.file.name}>
                  {t.file.name}
                </span>
                <span className="td-attachment__sub">
                  {formatBytes(t.file.size)}
                  {t.status === "uploading" ? ` · uploading ${t.progress}%` : ""}
                  {t.status === "done" ? " · added" : ""}
                </span>
                {t.status === "uploading" ? (
                  <span className="td-progress" aria-hidden="true">
                    <span className="td-progress__bar" style={{ width: `${t.progress}%` }} />
                  </span>
                ) : null}
                {t.status === "error" ? (
                  <button
                    type="button"
                    className="td-attachment__retry"
                    onClick={() => startUpload(t)}
                  >
                    Retry upload
                  </button>
                ) : null}
              </span>
              <button
                type="button"
                className="td-attachment__remove"
                onClick={() => removeTile(t)}
                aria-label={`Remove ${t.file.name}`}
              >
                <CloseIcon width={18} height={18} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
